import React from 'react';
import { Target, Users, School, CheckCheck, Flag, Lightbulb, Handshake, Scale, LineChart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import './PublicPages.css';

const principles = [
  {
    icon: Target,
    title: 'Outcome First',
    description: 'Har decision ka benchmark hota hai: kya isse student outcome improve hoga?'
  },
  {
    icon: Lightbulb,
    title: 'Practical Simplicity',
    description: 'Complex tech ko simple, usable workflows me convert karna humara core principle hai.'
  },
  {
    icon: Handshake,
    title: 'Trust Through Transparency',
    description: 'Progress signals clear hone chahiye taaki students aur parents dono confident rahein.'
  },
  {
    icon: Scale,
    title: 'Consistency Over Cramming',
    description: 'Long-term habits short-term panic se zyada strong performance dete hain.'
  }
];

const journey = [
  {
    title: 'Phase 1: Learning Gaps Ko Identify Karna',
    description: 'Humne pehle student struggle patterns dekhe: random prep, delayed feedback, low consistency.'
  },
  {
    title: 'Phase 2: Structured Practice Engine',
    description: 'Ek aisa system build kiya jo daily practice ko track kare aur weak spots ko priority de.'
  },
  {
    title: 'Phase 3: Family + Teacher Visibility',
    description: 'Parent and teacher alignment add kiya, jisse support ecosystem strong hua.'
  },
  {
    title: 'Phase 4: Outcome-Oriented AI Layer',
    description: 'Adaptive AI workflows integrate karke learning ko personalized aur measurable banaya.'
  }
];

const audience = [
  {
    icon: Users,
    title: 'Students',
    description: 'Daily plan, smart practice aur faster feedback se clear direction milti hai.'
  },
  {
    icon: School,
    title: 'Teachers',
    description: 'Class trend visibility aur targeted intervention ke liye focused insights milte hain.'
  },
  {
    icon: CheckCheck,
    title: 'Parents',
    description: 'Simple progress visibility se ghar par right support dena easy ho jata hai.'
  },
  {
    icon: LineChart,
    title: 'Institutions',
    description: 'Scalable monitoring ke saath academic quality improvement ko track kar sakte hain.'
  }
];

const AboutPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />
      <main className="public-page">
        <div className="public-page-shell">
          <section className="public-hero-card">
            <span className="public-kicker">About</span>
            <h1>ZapTick AI ka Mission: Better Learning Outcomes for Every Student</h1>
            <p>
              Humara focus simple hai: students ko structured learning path dena, teachers ko useful insights
              dena, aur parents ko clear progress visibility dena. Technology ka use tabhi meaningful hai jab
              woh classroom aur real study habits dono me measurable improvement create kare.
            </p>
          </section>

          <section className="public-section">
            <h2 className="public-section-title">Our North Star</h2>
            <p className="public-section-subtitle">
              Hum ek aisi learning platform build kar rahe hain jo students ko direction de, overwhelm nahi.
            </p>
          </section>

          <section className="public-columns">
            <article className="info-panel">
              <div className="public-card-icon">
                <Flag size={22} />
              </div>
              <h3>Mission</h3>
              <p>
                Every student ko guided academic progress dena through adaptive learning, timely feedback, and
                actionable daily goals.
              </p>
            </article>
            <article className="info-panel">
              <div className="public-card-icon">
                <Target size={22} />
              </div>
              <h3>Vision</h3>
              <p>
                Aisa ecosystem jahan student effort, teacher strategy aur parent support seamlessly connected ho.
              </p>
            </article>
          </section>

          <section className="public-section section-gap-top">
            <h2 className="public-section-title">The Principles Behind ZapTick AI</h2>
          </section>

          <section className="public-grid">
            {principles.map((point) => (
              <article key={point.title} className="public-card">
                <div className="public-card-icon">
                  <point.icon size={22} />
                </div>
                <h3>{point.title}</h3>
                <p>{point.description}</p>
              </article>
            ))}
          </section>

          <section className="public-section section-gap-top">
            <h2 className="public-section-title">How We Evolved</h2>
            <p className="public-section-subtitle">
              Product ki journey student pain points se start hui aur iterative improvements se mature hui.
            </p>
          </section>

          <section className="timeline-list">
            {journey.map((milestone) => (
              <article key={milestone.title} className="timeline-item">
                <span className="timeline-dot" />
                <div>
                  <h3>{milestone.title}</h3>
                  <p>{milestone.description}</p>
                </div>
              </article>
            ))}
          </section>

          <section className="public-section section-gap-top">
            <h2 className="public-section-title">Who We Serve</h2>
          </section>

          <section className="public-grid">
            {audience.map((group) => (
              <article key={group.title} className="public-card info-emphasis-card">
                <div className="public-card-icon">
                  <group.icon size={22} />
                </div>
                <h3>{group.title}</h3>
                <p>{group.description}</p>
              </article>
            ))}
          </section>

          <section className="public-cta-card">
            <h2>Join the Platform and Experience It Yourself</h2>
            <p>Account create karke aap guided onboarding ke saath turant start kar sakte hain.</p>
            <button className="public-primary-btn" onClick={() => navigate('/register')}>
              Create Account
            </button>
          </section>
        </div>
      </main>
    </>
  );
};

export default AboutPage;
